import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { PrintersApiAdapter, PrintersFilters } from '../adapters/PrintersApiAdapter';

export const usePrinters = (filters: PrintersFilters & { enabled?: boolean } = {}) => {
  const { enabled = true, ...restFilters } = filters;
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PrintersApiAdapter(httpClient);

  // 🎯 Crear queryKey estable para evitar re-renders infinitos y conflictos
  const queryKey = useMemo(() => {
    const { searchTerm, sortBy, sortDirection, status, active } = restFilters;
    return [
      'printersModule', // 🎯 Cambiar para evitar conflicto con módulo deprecated
      searchTerm || '',
      sortBy || 'id',
      sortDirection || 'desc',
      status || '',
      active || '',
    ];
  }, [restFilters]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useInfiniteQuery({
    queryKey, // Used the memoized queryKey
    queryFn: ({ pageParam = 1 }) =>
      apiAdapter.getPrintersPaginated({
        ...restFilters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 1 * 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    enabled, // Solo ejecutar cuando enabled es true
  });

  // 🎯 Flatten all pages into a single array (memoizado para evitar re-renders)
  const printers = useMemo(() => {
    const result = data?.pages.flatMap((page) => page.printers) || [];
    console.log('🔍 usePrinters - Flattened printers:', {
      pages: data?.pages?.length || 0,
      printersCount: result.length,
      enabled,
    });
    return result;
  }, [data?.pages, enabled]);

  const total = useMemo(() => {
    const result = data?.pages[0]?.total || 0;
    console.log('🔍 usePrinters - Total:', result);
    return result;
  }, [data?.pages]);

  console.log('🔍 usePrinters - Return values:', {
    printersCount: printers.length,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    total,
    nextPage: data?.pages?.[data.pages.length - 1]?.nextPage,
  });

  return {
    printers,
    isLoading,
    error: error?.message || null,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage || false,
    isFetchingNextPage,
    total,
  };
};
